import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { database, auth, functions, storage } from "../firebase";
import { useForAuthorisation } from "../context";
import { handleAIResponse } from "./ChatAI";
import { writeLiveChatInfo } from "./liveChatMeta";
import "./ChatWidget.css";

const QUICK_ACTIONS = [
  {
    label: "Data Entry >> Accounts",
    prompt: "How do I upload and allocate transactions?",
  },
  {
    label: "Tax >> VAT Calculation",
    prompt: "How do I run VAT calculation for this period?",
  },
  {
    label: "Invoicing >> Invoices",
    prompt: "How do I create and send an invoice?",
  },
  {
    label: "Payroll >> Payroll Dashboard",
    prompt: "How do I run payroll for this month?",
  },
  {
    label: "Reports >> Trial Balance",
    prompt: "How do I check the trial balance?",
  },
];

const STATUS_LABELS = {
  reviewing: "Reviewing",
  in_progress: "In Progress",
  awaiting_user: "Reviewing",
};

const RTDB_SERVER_TIMESTAMP = { ".sv": "timestamp" };

const summarizeMessageForReply = (message = {}) => {
  if (message.imageUrl && !message.text) {
    return "[Image]";
  }

  const text = (message.text || "").replace(/\s+/g, " ").trim();
  if (!text) {
    return "[No text]";
  }

  return text.length > 140 ? `${text.slice(0, 140)}...` : text;
};

const splitTableCells = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isTableSeparatorLine = (line) =>
  /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const renderInlineChatText = (text, history, keyPrefix) => {
  const parts = text.split(
    /(\[[^\]]+\]\((?:https?:\/\/|\/)[^)]+\)|https?:\/\/[^\s<]+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|\*\*[^*]+\*\*|\/[A-Z][A-Za-z0-9]+)/g,
  );

  return parts.map((part, i) => {
    const key = `${keyPrefix}-inline-${i}`;
    const markdownLinkMatch = part.match(
      /^\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)$/,
    );

    if (markdownLinkMatch) {
      const [, label, url] = markdownLinkMatch;

      if (url.startsWith("/")) {
        return (
          <button
            type="button"
            key={key}
            onClick={() => history && history.push(url)}
            className="chat-route-link"
            title={`Go to ${url}`}
          >
            <span>{label}</span>
            <span aria-hidden="true" className="chat-route-link-arrow">→</span>
          </button>
        );
      }

      return (
        <a className="chat-external-link" key={key} href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    }

    if (/^https?:\/\//.test(part)) {
      const trailingPunctuation = part.match(/[.,;!?]+$/)?.[0] || "";
      const url = trailingPunctuation
        ? part.slice(0, -trailingPunctuation.length)
        : part;
      return (
        <React.Fragment key={key}>
          <a className="chat-external-link" href={url} target="_blank" rel="noopener noreferrer">
            Open link ↗
          </a>
          {trailingPunctuation}
        </React.Fragment>
      );
    }

    if (/^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return <a className="chat-external-link" key={key} href={`mailto:${part}`}>{part}</a>;
    }

    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }

    if (/^\/[A-Z][A-Za-z0-9]+$/.test(part)) {
      return (
        <button
          type="button"
          key={key}
          onClick={() => history && history.push(part)}
          className="chat-route-link chat-route-link-compact"
          title={`Go to ${part}`}
        >
          {part}
        </button>
      );
    }

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
};

// Renders chat message text with basic markdown: links, bold text, route pills, and markdown tables.
const renderChatText = (text, history) => {
  if (!text) return null;

  const lines = text.split("\n");
  const blocks = [];

  for (let i = 0; i < lines.length; i += 1) {
    const headerLine = lines[i] || "";
    const separatorLine = lines[i + 1] || "";

    if (headerLine.includes("|") && isTableSeparatorLine(separatorLine)) {
      const headers = splitTableCells(headerLine);
      const rows = [];
      let rowIndex = i + 2;

      while (
        rowIndex < lines.length &&
        lines[rowIndex].trim() !== "" &&
        lines[rowIndex].includes("|")
      ) {
        rows.push(splitTableCells(lines[rowIndex]));
        rowIndex += 1;
      }

      blocks.push(
        <div
          key={`table-wrap-${i}`}
          style={{ overflowX: "auto", margin: "8px 0" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.9em",
              lineHeight: 1.35,
            }}
          >
            <thead>
              <tr>
                {headers.map((cell, cellIndex) => (
                  <th
                    key={`th-${i}-${cellIndex}`}
                    style={{
                      textAlign: "left",
                      border: "1px solid #d1d5db",
                      padding: "6px 8px",
                      background: "#f8fafc",
                    }}
                  >
                    {renderInlineChatText(
                      cell,
                      history,
                      `th-${i}-${cellIndex}`,
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={`tr-${i}-${rIdx}`}>
                  {headers.map((_, cIdx) => (
                    <td
                      key={`td-${i}-${rIdx}-${cIdx}`}
                      style={{
                        border: "1px solid #d1d5db",
                        padding: "6px 8px",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInlineChatText(
                        row[cIdx] || "",
                        history,
                        `td-${i}-${rIdx}-${cIdx}`,
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );

      i = rowIndex - 1;
      continue;
    }

    blocks.push(
      <React.Fragment key={`line-${i}`}>
        {renderInlineChatText(headerLine, history, `line-${i}`)}
        {i < lines.length - 1 ? <br /> : null}
      </React.Fragment>,
    );
  }

  return blocks;
};

// Utility function to calculate data size in human readable format
const calculateDataSize = (data) => {
  const jsonString = JSON.stringify(data);
  const bytes = new Blob([jsonString]).size;

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const normalizeSelectionValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value).trim();
};

const buildSelectedCompanyContext = (selection = {}) => {
  const selectedCompanyId = normalizeSelectionValue(selection.companySelected);
  const selectedCompanyKey = normalizeSelectionValue(
    selection.sameCompanyKeyValue,
  );
  const selectedCompanyName = normalizeSelectionValue(selection.companyName);
  const selectedYear = normalizeSelectionValue(selection.yearSelected);

  if (
    !selectedCompanyId &&
    !selectedCompanyKey &&
    !selectedCompanyName &&
    !selectedYear
  ) {
    return null;
  }

  return {
    selectedCompanyId,
    selectedCompanyKey,
    selectedCompanyName,
    selectedYear,
  };
};

const ChatWidget = ({ isVisible, onToggle }) => {
  const { emailDetails, MainUserUID } = useForAuthorisation();
  const history = useHistory();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isAITyping, setIsAITyping] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatStarted, setChatStarted] = useState(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const [lastTap, setLastTap] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [chatStatus, setChatStatus] = useState("reviewing");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [metaSnapshot, setMetaSnapshot] = useState(null);
  const [replyContext, setReplyContext] = useState(null);
  const [selectedCompanyContext, setSelectedCompanyContext] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const chatWidgetRef = useRef(null);
  const imageInputRef = useRef(null);
  const adminResponseTimerRef = useRef(null);
  const lastUserMessageTimestampRef = useRef(null);
  const lastUserMessageTextRef = useRef(null);
  const lastCompanyContextSignatureRef = useRef("");
  const chatId =
    auth?.currentUser?.uid || MainUserUID || "anonymous_" + Date.now();

  useEffect(() => {
    if (!chatId || !chatStarted) {
      return undefined;
    }

    const presenceRef = database.ref(`liveChat/${chatId}/presence/user`);
    const connectedRef = database.ref(".info/connected");
    let heartbeatInterval = null;
    let connectedListener = null;

    const setUserPresence = (online) => {
      presenceRef.update({
        online,
        lastSeen: Date.now(),
      });
    };

    const handleVisibilityChange = () => {
      setUserPresence(!document.hidden);
    };

    const handleBeforeUnload = () => {
      setUserPresence(false);
    };

    connectedListener = connectedRef.on("value", (snapshot) => {
      if (snapshot.val() === true) {
        // Ensure presence is corrected if tab/app closes unexpectedly.
        presenceRef.onDisconnect().update({
          online: false,
          lastSeen: RTDB_SERVER_TIMESTAMP,
        });
      }
    });

    setUserPresence(!document.hidden);
    heartbeatInterval = setInterval(() => {
      if (!document.hidden) {
        setUserPresence(true);
      }
    }, 20000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
      if (connectedListener) {
        connectedRef.off("value", connectedListener);
      }
      presenceRef.onDisconnect().cancel();
      setUserPresence(false);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [chatId, chatStarted]);

  useEffect(() => {
    const selectionUid =
      auth?.currentUser?.uid ||
      (typeof MainUserUID === "string" ? MainUserUID.trim() : "");

    if (!selectionUid || !chatId) {
      return undefined;
    }

    const userSelectionRef = database.ref(`/UserSelections/${selectionUid}`);
    const handleSelectionSnapshot = (snapshot) => {
      const context = buildSelectedCompanyContext(snapshot.val() || {});
      setSelectedCompanyContext(context);

      if (!chatStarted || !context) {
        return;
      }

      const signature = JSON.stringify(context);
      if (signature === lastCompanyContextSignatureRef.current) {
        return;
      }

      lastCompanyContextSignatureRef.current = signature;
      writeLiveChatInfo(chatId, {
        ...context,
        selectedCompanyUpdatedAt: Date.now(),
      }).catch((error) => {
        console.warn("Failed to sync selected company context for live chat:", error);
      });
    };

    userSelectionRef.on("value", handleSelectionSnapshot);

    return () => {
      userSelectionRef.off("value", handleSelectionSnapshot);
    };
  }, [MainUserUID, chatId, chatStarted]);

  // Check if user is admin
  useEffect(() => {
    const email = emailDetails?.toLowerCase() || "";
    setIsAdmin(email.includes("filingstack"));
  }, [emailDetails]);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Listen for messages — last 2 days only (server-side filter)
  useEffect(() => {
    if (chatId) {
      const twoDaysAgo = Date.now() - 2 * 24 * 60 * 60 * 1000;
      // orderByChild('timestamp') requires .indexOn ["timestamp"] in database rules
      const messagesRef = database
        .ref(`liveChat/${chatId}/messages`)
        .orderByChild("timestamp")
        .startAt(twoDaysAgo);

      messagesRef.on("value", (snapshot) => {
        const messagesData = snapshot.val();

        if (messagesData) {
          const messagesList = Object.keys(messagesData)
            .map((key) => ({
              id: key,
              ...messagesData[key],
            }))
            .sort((a, b) => a.timestamp - b.timestamp);

          setMessages(messagesList);
          setChatStarted(true);

          // Cancel pending AI fallback timer if admin replied
          if (
            adminResponseTimerRef.current &&
            lastUserMessageTimestampRef.current
          ) {
            const hasAdminReply = messagesList.some(
              (msg) =>
                msg.sender === "admin" &&
                msg.timestamp > lastUserMessageTimestampRef.current,
            );
            if (hasAdminReply) {
              clearTimeout(adminResponseTimerRef.current);
              adminResponseTimerRef.current = null;
              setIsAITyping(false);
            }
          }

          // Count unread messages from admin AND ai
          if (!isVisible) {
            const unread = messagesList.filter(
              (msg) =>
                (msg.sender === "admin" || msg.sender === "ai") && !msg.read,
            ).length;
            setUnreadCount(unread);
          }
        }
      });

      // Listen for admin online status
      const adminStatusRef = database.ref("adminOnlineStatus");
      adminStatusRef.on("value", (snapshot) => {
        const status = snapshot.val();
        setAdminOnline(status?.online || false);
      });

      // Listen for chat metadata/status
      const metaRef = database.ref(`liveChatMeta/${chatId}`);
      metaRef.on("value", (snapshot) => {
        const value = snapshot.val() || null;
        setMetaSnapshot(value);
        if (value?.status) {
          setChatStatus(value.status);
        }
        if (value?.feedbackSubmitted) {
          setFeedbackSubmitted(true);
        }
      });

      // Listen for typing indicators
      const typingRef = database.ref(`liveChat/${chatId}/typing/admin`);
      typingRef.on("value", (snapshot) => {
        setIsTyping(snapshot.val() || false);
      });

      return () => {
        messagesRef.off();
        adminStatusRef.off();
        metaRef.off();
        typingRef.off();
        if (adminResponseTimerRef.current) {
          clearTimeout(adminResponseTimerRef.current);
          adminResponseTimerRef.current = null;
        }
      };
    }
  }, [chatId, isVisible]);

  // Mark messages as read when chat is visible
  useEffect(() => {
    if (isVisible && chatId) {
      setUnreadCount(0);
      const updates = {};
      messages.forEach((msg) => {
        if ((msg.sender === "admin" || msg.sender === "ai") && !msg.read) {
          updates[`liveChat/${chatId}/messages/${msg.id}/read`] = true;
        }
      });
      if (Object.keys(updates).length > 0) {
        database.ref().update(updates);
      }
    }
  }, [isVisible, messages, chatId]);

  useEffect(() => {
    if (!messages.length || !chatId) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender === "user") {
      setChatStatus("reviewing");
    } else if (lastMessage.sender === "admin" || lastMessage.sender === "ai") {
      setChatStatus("awaiting_user");
    }

    const firstUserMessage = messages.find((msg) => msg.sender === "user");
    const firstSupportMessage = messages.find(
      (msg) => msg.sender === "admin" || msg.sender === "ai",
    );

    if (
      firstUserMessage?.timestamp &&
      firstSupportMessage?.timestamp &&
      !metaSnapshot?.firstResponseMs
    ) {
      writeLiveChatInfo(chatId, {
        firstResponseMs: Math.max(
          0,
          Number(firstSupportMessage.timestamp) -
            Number(firstUserMessage.timestamp),
        ),
      }).catch((error) => {
        console.warn("Failed to save first response metric:", error);
      });
    }
  }, [messages, chatId, metaSnapshot?.firstResponseMs]);

  useEffect(() => {
    if (isTyping) {
      setChatStatus("in_progress");
    }
  }, [isTyping]);
  //  console.log(adminOnline);

  const startEditMessage = (message) => {
    setEditingMessageId(message.id);
    setEditingText(message.text);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditingText("");
  };

  const saveEdit = async (messageId) => {
    if (!editingText.trim()) return;

    try {
      await database.ref(`liveChat/${chatId}/messages/${messageId}`).update({
        text: editingText,
        edited: true,
        editedAt: Date.now(),
      });
      setEditingMessageId(null);
      setEditingText("");
    } catch (error) {
      console.error("Error editing message:", error);
      alert("Failed to edit message. Please try again.");
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      await database.ref(`liveChat/${chatId}/messages/${messageId}`).remove();
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message. Please try again.");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only image files are supported (JPEG, PNG, GIF, WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB.");
      return;
    }
    setUploadingImage(true);
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storageRef = storage.ref(
        `chatImages/${chatId}/${timestamp}_${safeName}`,
      );
      const snapshot = await storageRef.put(file);
      const downloadURL = await snapshot.ref.getDownloadURL();

      const messageData = {
        text: "",
        imageUrl: downloadURL,
        imageType: "image",
        sender: "user",
        timestamp,
        senderEmail: emailDetails || "anonymous@example.com",
        senderName: emailDetails?.split("@")[0] || "User",
        read: false,
      };

      await database.ref(`liveChat/${chatId}/messages`).push(messageData);
      await writeLiveChatInfo(
        chatId,
        {
          lastMessage: "📷 Image",
          lastMessageTime: timestamp,
          userEmail: emailDetails || "anonymous@example.com",
          userId: MainUserUID || chatId,
          status: "active",
          unreadCount: 1, // admin hasn't read yet
          ...(selectedCompanyContext || {}),
          selectedCompanyUpdatedAt: Date.now(),
        },
        "set",
      );
      database.ref(`liveChat/${chatId}/typing/user`).set(false);

      try {
        const EmailOnChatMessage = functions.httpsCallable(
          "EmailOnSupportRequestMessage",
        );
        await EmailOnChatMessage({
          id: MainUserUID || chatId,
          emailSubject: `Live Chat Image from ${emailDetails || "Anonymous User"}`,
          emailMessage: `User ${emailDetails?.split("@")[0] || "Anonymous"} sent an image in live chat.\n\nImage: ${downloadURL}\n\nChat ID: ${chatId}`,
          emailHTML: `<p>User <strong>${emailDetails || "Anonymous"}</strong> sent an image in live chat:</p><p><a href="${downloadURL}">View Screenshot</a></p><p><img src="${downloadURL}" style="max-width:400px;border-radius:8px;" /></p><p><em>Chat ID: ${chatId}</em></p>`,
        });
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
      }

      if (!adminOnline) setIsAITyping(true);
      setTimeout(() => {
        handleAIResponse(
          "📷 [Image attachment]",
          messages,
          adminOnline,
          chatId,
        ).finally(() => setIsAITyping(false));
      }, 1000);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const sendMessage = async (overrideText = "") => {
    const normalizedOverride =
      typeof overrideText === "string" ? overrideText : "";
    const outboundText = (normalizedOverride || newMessage || "").trim();
    if (!outboundText) return;

    const activeReply = replyContext
      ? {
          messageId: replyContext.messageId,
          sender: replyContext.sender,
          text: replyContext.text,
          timestamp: replyContext.timestamp,
        }
      : null;

    const messageData = {
      text: outboundText,
      sender: "user",
      timestamp: Date.now(),
      senderEmail: emailDetails || "anonymous@example.com",
      senderName: emailDetails?.split("@")[0] || "User",
      read: false,
      ...(activeReply ? { replyTo: activeReply } : {}),
    };

    const messageText = outboundText; // Store before clearing
    setNewMessage("");
    setReplyContext(null);
    setChatStatus("reviewing");
    lastUserMessageTimestampRef.current = Date.now();
    lastUserMessageTextRef.current = messageText;
    if (!adminOnline) setIsAITyping(true);

    try {
      await database.ref(`liveChat/${chatId}/messages`).push(messageData);

      // Update chat info and lightweight summary
      await writeLiveChatInfo(
        chatId,
        {
          lastMessage: messageText,
          lastMessageTime: Date.now(),
          userEmail: emailDetails || "anonymous@example.com",
          userId: MainUserUID || chatId,
          status: "reviewing",
          unreadCount: 1, // admin hasn't read yet
          userMessageCount: Number(metaSnapshot?.userMessageCount || 0) + 1,
          totalMessageCount: Number(metaSnapshot?.totalMessageCount || 0) + 1,
          awaitingSince: Date.now(),
          ...(selectedCompanyContext || {}),
          selectedCompanyUpdatedAt: Date.now(),
        },
        "set",
      );

      // Clear user typing indicator
      database.ref(`liveChat/${chatId}/typing/user`).set(false);

      // Send email notification to FilingStack
      try {
        const EmailOnChatMessage = functions.httpsCallable(
          "EmailOnSupportRequestMessage",
        );
        await EmailOnChatMessage({
          id: MainUserUID || chatId,
          emailSubject: `Live Chat Message from ${
            emailDetails || "Anonymous User"
          }`,
          emailMessage: `User ${
            emailDetails?.split("@")[0] || "Anonymous"
          } sent a live chat message:\n\n${messageText}\n\nChat ID: ${chatId}`,
          emailHTML: `<p>User <strong>${
            emailDetails || "Anonymous"
          }</strong> sent a live chat message:</p><p>${messageText.replace(
            /\n/g,
            "<br />",
          )}</p><p><em>Chat ID: ${chatId}</em></p>`,
        });
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
        // Don't block message sending if email fails
      }

      // Trigger AI response after a short delay
      if (!adminOnline) {
        setTimeout(() => {
          handleAIResponse(messageText, messages, false, chatId)
            .then((detail) => {
              if (detail?.needsHumanSupport) {
                setChatStatus("reviewing");
              } else {
                setChatStatus("awaiting_user");
              }
            })
            .finally(() => setIsAITyping(false));
        }, 1000);
      } else {
        // Admin is online — wait 30s for their reply before AI steps in
        if (adminResponseTimerRef.current)
          clearTimeout(adminResponseTimerRef.current);
        adminResponseTimerRef.current = setTimeout(() => {
          adminResponseTimerRef.current = null;
          setIsAITyping(true);
          handleAIResponse(messageText, messages, false, chatId)
            .then((detail) => {
              if (detail?.needsHumanSupport) {
                setChatStatus("reviewing");
              } else {
                setChatStatus("awaiting_user");
              }
            })
            .finally(() => setIsAITyping(false));
        }, 30000);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleTyping = (text) => {
    setNewMessage(text);

    // Set typing indicator
    database.ref(`liveChat/${chatId}/typing/user`).set(true);

    // Clear typing indicator after delay
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      database.ref(`liveChat/${chatId}/typing/user`).set(false);
    }, 2000);
  };

  const sendQuickAction = (prompt) => {
    if (!prompt) return;
    sendMessage(prompt);
  };

  const beginReplyToMessage = (message) => {
    if (!message?.id) return;
    setReplyContext({
      messageId: message.id,
      sender: message.sender,
      text: summarizeMessageForReply(message),
      timestamp: message.timestamp,
    });
  };

  const renderReplyPreview = (replyTo) => {
    if (!replyTo) return null;

    const senderLabel =
      replyTo.sender === "admin"
        ? "Support"
        : replyTo.sender === "ai"
          ? "AI"
          : "You";

    return (
      <div
        style={{
          marginBottom: "6px",
          padding: "6px 8px",
          borderLeft: "3px solid #93c5fd",
          background: "#eff6ff",
          borderRadius: "6px",
          color: "#1e3a8a",
          fontSize: "12px",
          lineHeight: 1.35,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: "2px" }}>
          Replying to {senderLabel}
        </div>
        <div style={{ opacity: 0.95 }}>
          {replyTo.text || "[Referenced message]"}
        </div>
      </div>
    );
  };

  const renderUserReadTicks = (message) => {
    const isRead = !!message.read;
    const tick = isRead ? "✓✓" : "✓";

    return (
      <span
        title={isRead ? "Read" : "Sent"}
        style={{
          fontSize: "11px",
          fontWeight: 700,
          color: isRead ? "#0ea5e9" : "rgba(255,255,255,0.85)",
          marginLeft: "6px",
          letterSpacing: "0.02em",
        }}
      >
        {tick}
      </span>
    );
  };

  const submitFeedback = async (helpful) => {
    try {
      await database.ref(`liveChat/${chatId}/feedback`).push({
        helpful,
        timestamp: Date.now(),
        userEmail: emailDetails || "anonymous@example.com",
      });

      await writeLiveChatInfo(chatId, {
        feedbackSubmitted: true,
        lastFeedbackHelpful: helpful,
        lastFeedbackTime: Date.now(),
      });

      setFeedbackSubmitted(true);
    } catch (error) {
      console.error("Failed to submit chat feedback:", error);
    }
  };

  // Mobile-specific close handlers
  const handleTouchStart = (e) => {
    if (isMobile) {
      setTouchStartY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = (e) => {
    if (isMobile) {
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;

      // If user swipes down from the top area (header area), close the chat
      if (touchStartY < 100 && deltaY < -50) {
        onToggle();
      }
    }
  };

  const handleDoubleTap = (e) => {
    if (isMobile) {
      const now = Date.now();
      const DOUBLE_TAP_DELAY = 300;

      if (lastTap && now - lastTap < DOUBLE_TAP_DELAY) {
        // Double tap detected, close chat
        onToggle();
      }
      setLastTap(now);
    }
  };

  const startChat = () => {
    setChatStarted(true);
    const welcomeMessage = {
      text: "Hello! How can we help you today?",
      sender: "system",
      timestamp: Date.now(),
      read: true,
    };

    database.ref(`liveChat/${chatId}/messages`).push(welcomeMessage);
    writeLiveChatInfo(
      chatId,
      {
        lastMessage: "Chat started",
        lastMessageTime: Date.now(),
        userEmail: emailDetails || "anonymous@example.com",
        userId: MainUserUID || chatId,
        status: "active",
        ...(selectedCompanyContext || {}),
        selectedCompanyUpdatedAt: Date.now(),
      },
      "set",
    );
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isVisible) {
    return (
      <div className="chat-widget-button" onClick={onToggle}>
        <div className="chat-icon">💬</div>
        {unreadCount > 0 && <div className="unread-badge">{unreadCount}</div>}
        <div className="chat-button-text">Live Chat</div>
      </div>
    );
  }

  return (
    <div
      className="chat-widget"
      ref={chatWidgetRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={handleDoubleTap}
    >
      {/* Emergency close button for mobile - always visible */}
      {isMobile && (
        <button
          className="mobile-emergency-close"
          onClick={onToggle}
          aria-label="Emergency close chat"
          title="Close Chat"
        >
          ×
        </button>
      )}

      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-title">
            <h3>💬 FilingStack Support</h3>
            <div className="admin-status" style={{ marginTop: 6 }}>
              <span
                className={`status-dot ${adminOnline ? "online" : ""}`}
              ></span>
              <span>
                {adminOnline ? "Admin reviewing" : "Reply expected shortly"}
              </span>
              <span>•</span>
              <span>{STATUS_LABELS[chatStatus] || "Reviewing"}</span>
            </div>
          </div>
          <button
            className="chat-close"
            onClick={onToggle}
            aria-label="Close chat"
          >
            ×
          </button>
        </div>
        {/* Mobile helper text */}
        {isMobile && (
          <div
            style={{
              fontSize: "10px",
              opacity: 0.8,
              textAlign: "center",
              marginTop: "4px",
              borderTop: "1px solid rgba(255,255,255,0.2)",
              paddingTop: "4px",
            }}
          >
            Swipe down from top or double-tap to close
          </div>
        )}
      </div>

      <div className="chat-body">
        {!chatStarted ? (
          <div className="chat-welcome">
            <div className="welcome-content">
              <h4>👋 Welcome to FilingStack Support</h4>
              <p>Get instant help with your accounting questions.</p>
              <button className="start-chat-btn" onClick={startChat}>
                Start Chat
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="messages-container">
              {!adminOnline && (
                <div
                  style={{
                    background: "#eff6ff",
                    color: "#1e40af",
                    border: "1px solid #bfdbfe",
                    borderRadius: 10,
                    padding: "8px 10px",
                    fontSize: 12,
                  }}
                >
                  Support will review this chat and respond shortly if required.
                </div>
              )}

              {messages.map((message) => (
                <div key={message.id} className={`message ${message.sender}`}>
                  {message.sender === "system" && (
                    <div className="system-message">
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {message.text}
                      </span>
                    </div>
                  )}

                  {message.sender === "user" && (
                    <div className="user-message">
                      <div className="message-content">
                        {renderReplyPreview(message.replyTo)}
                        {message.imageUrl && (
                          <a
                            href={message.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={message.imageUrl}
                              alt="Screenshot"
                              style={{
                                maxWidth: "100%",
                                borderRadius: "8px",
                                marginBottom: "4px",
                                cursor: "pointer",
                                display: "block",
                              }}
                            />
                          </a>
                        )}
                        {editingMessageId === message.id ? (
                          <div style={{ width: "100%" }}>
                            <textarea
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              style={{
                                width: "100%",
                                minHeight: "60px",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #ddd",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                resize: "vertical",
                              }}
                              autoFocus
                            />
                            <div
                              style={{
                                marginTop: "8px",
                                display: "flex",
                                gap: "8px",
                              }}
                            >
                              <button
                                onClick={() => saveEdit(message.id)}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "none",
                                  background: "#4CAF50",
                                  color: "white",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                ✓ Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "1px solid #ddd",
                                  background: "white",
                                  color: "#666",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p style={{ whiteSpace: "pre-wrap" }}>
                              {message.text}
                              {message.edited && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#999",
                                    fontStyle: "italic",
                                    marginLeft: "6px",
                                  }}
                                >
                                  (edited)
                                </span>
                              )}
                            </p>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginTop: "4px",
                              }}
                            >
                              <span
                                className="message-time"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                {formatTime(message.timestamp)}
                                {renderUserReadTicks(message)}
                              </span>
                              <div style={{ display: "flex", gap: "6px" }}>
                                {!isAdmin && (
                                  <button
                                    onClick={() => beginReplyToMessage(message)}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "none",
                                      background: "rgba(255,255,255,0.2)",
                                      color: "white",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      opacity: 0.8,
                                    }}
                                    onMouseOver={(e) =>
                                      (e.target.style.opacity = "1")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.opacity = "0.8")
                                    }
                                    title="Reply to this message"
                                  >
                                    Reply
                                  </button>
                                )}
                                <button
                                  onClick={() => startEditMessage(message)}
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    background: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    cursor: "pointer",
                                    fontSize: "11px",
                                    opacity: 0.7,
                                  }}
                                  onMouseOver={(e) =>
                                    (e.target.style.opacity = "1")
                                  }
                                  onMouseOut={(e) =>
                                    (e.target.style.opacity = "0.7")
                                  }
                                  title="Edit message"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteMessage(message.id)}
                                  style={{
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    background: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    cursor: "pointer",
                                    fontSize: "11px",
                                    opacity: 0.7,
                                  }}
                                  onMouseOver={(e) =>
                                    (e.target.style.opacity = "1")
                                  }
                                  onMouseOut={(e) =>
                                    (e.target.style.opacity = "0.7")
                                  }
                                  title="Delete message"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {message.sender === "admin" && (
                    <div className="admin-message">
                      <div className="admin-avatar">👨‍💼</div>
                      <div className="message-content">
                        <div className="sender-name">Support Agent</div>
                        {renderReplyPreview(message.replyTo)}
                        {message.imageUrl && (
                          <a
                            href={message.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={message.imageUrl}
                              alt="Screenshot"
                              style={{
                                maxWidth: "100%",
                                borderRadius: "8px",
                                marginBottom: "4px",
                                cursor: "pointer",
                                display: "block",
                              }}
                            />
                          </a>
                        )}
                        {message.text ? (
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {renderChatText(message.text, history)}
                          </div>
                        ) : null}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: "4px",
                          }}
                        >
                          <span className="message-time">
                            {formatTime(message.timestamp)}
                          </span>
                          {!isAdmin && (
                            <button
                              onClick={() => beginReplyToMessage(message)}
                              style={{
                                padding: "3px 8px",
                                borderRadius: "999px",
                                border: "1px solid #cbd5e1",
                                background: "#ffffff",
                                color: "#475569",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                              title="Reply to this message"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {message.sender === "ai" && (
                    <div className="admin-message">
                      <div className="admin-avatar">🤖</div>
                      <div className="message-content">
                        <div className="sender-name">AI Assistant</div>
                        {renderReplyPreview(message.replyTo)}
                        {editingMessageId === message.id ? (
                          <div style={{ width: "100%" }}>
                            <textarea
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              style={{
                                width: "100%",
                                minHeight: "80px",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #ddd",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                resize: "vertical",
                                marginTop: "8px",
                              }}
                              autoFocus
                            />
                            <div
                              style={{
                                marginTop: "8px",
                                display: "flex",
                                gap: "8px",
                              }}
                            >
                              <button
                                onClick={() => saveEdit(message.id)}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "none",
                                  background: "#4CAF50",
                                  color: "white",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                ✓ Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                style={{
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  border: "1px solid #ddd",
                                  background: "white",
                                  color: "#666",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ whiteSpace: "pre-wrap" }}>
                              {renderChatText(message.text, history)}
                              {message.edited && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#999",
                                    fontStyle: "italic",
                                    marginLeft: "6px",
                                  }}
                                >
                                  (edited)
                                </span>
                              )}
                            </div>
                            {message.actionSuggestions?.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "6px",
                                  marginTop: "10px",
                                }}
                              >
                                {message.actionSuggestions.map((action) => (
                                  <button
                                    key={`${message.id}-${action.id || action.route}`}
                                    type="button"
                                    onClick={() => history.push(action.route)}
                                    title={
                                      action.route.includes("chatAction=")
                                        ? "Open the page and apply this safe preset filter"
                                        : `Open ${action.label}`
                                    }
                                    style={{
                                      border: "1px solid #2563eb",
                                      borderRadius: "999px",
                                      padding: "6px 10px",
                                      background: "#eff6ff",
                                      color: "#1d4ed8",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {action.label} →
                                  </button>
                                ))}
                              </div>
                            )}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginTop: "4px",
                              }}
                            >
                              {isAdmin && (
                                <span className="message-time">
                                  {formatTime(message.timestamp)}
                                </span>
                              )}
                              {!isAdmin && (
                                <button
                                  onClick={() => beginReplyToMessage(message)}
                                  style={{
                                    padding: "3px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #cbd5e1",
                                    background: "#ffffff",
                                    color: "#475569",
                                    cursor: "pointer",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                  }}
                                  title="Reply to this message"
                                >
                                  Reply
                                </button>
                              )}
                              {isAdmin && (
                                <div style={{ display: "flex", gap: "6px" }}>
                                  <button
                                    onClick={() => startEditMessage(message)}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      background: "white",
                                      color: "#666",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      opacity: 0.7,
                                    }}
                                    onMouseOver={(e) =>
                                      (e.target.style.opacity = "1")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.opacity = "0.7")
                                    }
                                    title="Edit AI response"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    onClick={() => deleteMessage(message.id)}
                                    style={{
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      border: "1px solid #e0e0e0",
                                      background: "white",
                                      color: "#dc2626",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      opacity: 0.7,
                                    }}
                                    onMouseOver={(e) =>
                                      (e.target.style.opacity = "1")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.opacity = "0.7")
                                    }
                                    title="Delete AI response"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isTyping && (
                <div className="typing-indicator">
                  <div className="admin-avatar">👨‍💼</div>
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              {isAITyping && (
                <div className="typing-indicator">
                  <div className="admin-avatar">🤖</div>
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input">
              {replyContext && (
                <div
                  style={{
                    marginBottom: "8px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    border: "1px solid #bfdbfe",
                    background: "#eff6ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#1e3a8a" }}>
                    <strong>
                      Replying to{" "}
                      {replyContext.sender === "user"
                        ? "You"
                        : replyContext.sender === "admin"
                          ? "Support"
                          : "AI"}
                      :
                    </strong>{" "}
                    {replyContext.text}
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyContext(null)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      color: "#1e40af",
                      fontWeight: 700,
                    }}
                    title="Cancel reply"
                  >
                    ×
                  </button>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    onClick={() => sendQuickAction(action.prompt)}
                    style={{
                      border: "1px solid #d1d5db",
                      background: "#f8fafc",
                      color: "#334155",
                      borderRadius: 999,
                      padding: "4px 10px",
                      fontSize: 11,
                      cursor: "pointer",
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>

              <div className="input-container">
                <input
                  type="file"
                  ref={imageInputRef}
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) uploadImage(file);
                  }}
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="image-upload-btn"
                  disabled={uploadingImage}
                  title="Send a screenshot or image"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: uploadingImage ? "not-allowed" : "pointer",
                    fontSize: "18px",
                    padding: "0 6px",
                    opacity: uploadingImage ? 0.5 : 0.75,
                    flexShrink: 0,
                  }}
                >
                  {uploadingImage ? "⏳" : "📷"}
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => handleTyping(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type your message..."
                  className="message-input"
                />
                <button
                  onClick={sendMessage}
                  className="send-button"
                  disabled={!newMessage.trim()}
                >
                  📤
                </button>
              </div>
              <div className="chat-footer">
                {!feedbackSubmitted &&
                messages.some(
                  (m) => m.sender === "ai" || m.sender === "admin",
                ) ? (
                  <small
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    Helpful?
                    <button
                      type="button"
                      onClick={() => submitFeedback(true)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => submitFeedback(false)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      No
                    </button>
                  </small>
                ) : (
                  <small>
                    {feedbackSubmitted ? "Thanks for your feedback" : ""}
                  </small>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
